package one.healthium.bland.presentation.feature.today

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.AnimationVector1D
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.stateDescription
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.center
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.util.fastForEachIndexed
import androidx.lifecycle.compose.LifecycleResumeEffect
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import one.healthium.onboarding.ui.components.drawTriangle
import one.healthium.onboarding.ui.components.tooltipTriangleHeight
import one.healthium.onboarding.ui.components.tooltipTriangleWidth
import one.healthium.today.ui.TodayUiState
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.Themes
import one.healthium.ui.compose.theme.red_8
import one.healthium.ui.compose.theme.white
import one.healthium.ui.compose.theme.white50
import one.healthium.ui.compose.theme.yellow_9
import androidx.compose.ui.tooling.preview.Preview
import kotlin.math.max
import kotlin.math.min
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes

private const val debug = false // draw debug lines

@Composable
fun CaloriesChart(
    state: TodayUiState,
    onAddFoodClicked: () -> Unit,
) {
    var now by remember { mutableStateOf(Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())) }
    val scope = rememberCoroutineScope()

    LifecycleResumeEffect(Unit) {
        val job = scope.launch {
            while (isActive) {
                now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
                delay(1.minutes.inWholeMilliseconds)
            }
        }
        onPauseOrDispose {
            job.cancel()
        }
    }
    val today = now.date
    val textMeasurer = rememberTextMeasurer()
    val weekDayLabelStyle = AppTheme.textStyles.secondary_12_600
    val weekDayLabelStyleActive = AppTheme.textStyles.secondary_12_700
    var isEmpty by remember { mutableStateOf(false) }

    val currentWeek = remember(state, today) {
        val foods = state.foodLogs?.sortedBy { it.date }
        isEmpty = foods?.isEmpty() == true

        when {
            foods == null -> return@remember emptyList()
            foods.isEmpty() -> ChartMapper.buildEmptyChartList(
                today = today,
                textMeasurer = textMeasurer,
                labelStyle = weekDayLabelStyle,
            )

            else -> ChartMapper.buildChartList(
                foods = foods,
                today = today,
                textMeasurer = textMeasurer,
                labelStyle = weekDayLabelStyle,
                labelStyleActive = weekDayLabelStyleActive,
            )
        }
    }

    val strokeProgress = remember { Animatable(0f) }

    LaunchedEffect(currentWeek) {
        strokeProgress.animateTo(1f, tween(800))
    }

    Box {
        Chart(
            points = currentWeek,
            strokeProgress = strokeProgress,
        )
        if (isEmpty) {
            LogFoodButton(
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .padding(top = 36.dp)
                    .testTag("log_food_button"),
                onAddFoodClicked = onAddFoodClicked
            )
        }
    }
}

private val chartHorizontalPadding = 24.dp

@Composable
private fun Chart(
    points: List<ChartPoint>,
    strokeProgress: Animatable<Float, AnimationVector1D> = Animatable(1f),
) {
    BoxWithConstraints {
        val pointsGap = (maxWidth - (chartHorizontalPadding * 2)) / 6
        val pages = (points.size / 6)
        val chartWidth = (maxWidth * pages + pointsGap * (pages - 1)) - (chartHorizontalPadding * 2 * (pages - 1))
        var tapOffset by remember { mutableStateOf<Offset?>(null) }

        val semanticState = remember(points) {
            if (points.isEmpty()) {
                "No data available"
            } else {
                val activePoint = points.firstOrNull { it.active }
                activePoint?.displayValue?.layoutInput?.text?.text ?: "Tap on a point to see details"
            }
        }

        Canvas(
            modifier = Modifier
                .horizontalScroll(state = rememberScrollState(), reverseScrolling = true)
                .width(chartWidth) // additional gap between sunday and monday
                .padding(top = 48.dp)
                .padding(horizontal = chartHorizontalPadding)
                .height(217.dp)
                .pointerInput(points) {
                    detectTapGestures { offset ->
                        tapOffset = offset
                    }
                }
                .semantics {
                    testTag = "calories_chart"
                    stateDescription = semanticState
                }
        ) {
            val graphHeight = size.height - 20.sp.toPx()
            val pointsGap = pointsGap.toPx()

            val calculateX = { index: Int ->
                index * pointsGap
            }
            val calculateY = { value: Float ->
                graphHeight - value * graphHeight * strokeProgress.value
            }

            val strokePath = Path().apply {
                for (i in 0 until points.size - 1) {
                    val chartPoint = points[i]

                    val x1 = calculateX(i)
                    val y1 = calculateY(chartPoint.value)

                    if (i == 0) {
                        moveTo(x1, y1)
                    }
                    val x2 = calculateX(i + 1)
                    val y2 = calculateY(points[i + 1].value)

                    val cx = (x1 + x2) / 2f
                    cubicTo(x1 = cx, y1 = y1, x2 = cx, y2 = y2, x3 = x2, y3 = y2)
                }
            }

            drawPath(
                path = strokePath,
                color = white,
                style = Stroke(
                    width = 2.dp.toPx(),
                    cap = StrokeCap.Round
                )
            )

            if (strokePath.isEmpty.not()) {
                val fillPath = Path().apply {
                    addPath(strokePath)
                    lineTo(size.width, graphHeight)
                    lineTo(0f, graphHeight)
                    close()
                }

                drawPath(
                    path = fillPath,
                    color = Color.White.copy(alpha = 0.25f)
                )
            }

            points.fastForEachIndexed { index, point ->
                val x = calculateX(index)
                val y = calculateY(point.value)
                val tapArea = Rect(
                    left = x - pointsGap / 2f,
                    top = 0f,
                    right = x + pointsGap / 2f,
                    bottom = graphHeight,
                )

                if (debug) {
                    drawRect(
                        color = Color.White,
                        topLeft = tapArea.topLeft,
                        size = tapArea.size,
                        style = Stroke(width = 3f)
                    )
                }

                val tapOffset = tapOffset
                if (point.displayValue.size.width > 0) {
                    if (tapOffset != null && tapArea.contains(tapOffset) || point.active) {
                        val dotPoint = Offset(x, y)

                        drawLine(
                            brush = SolidColor(red_8),
                            start = dotPoint,
                            end = Offset(x, graphHeight),
                            strokeWidth = 1.26.dp.toPx(),
                            pathEffect = PathEffect.dashPathEffect(
                                intervals = floatArrayOf(2.dp.toPx(), 2.dp.toPx()),
                                phase = 0f
                            )
                        )

                        drawCircle(
                            brush = SolidColor(red_8),
                            radius = 8.dp.toPx(),
                            center = dotPoint,
                        )
                        drawCircle(
                            brush = SolidColor(white),
                            radius = 6.dp.toPx(),
                            center = dotPoint,
                        )

                        drawTooltip(
                            measuredText = point.displayValue,
                            point = dotPoint,
                        )
                    }
                }

                drawText(
                    textLayoutResult = point.label,
                    color = if (point.active) white else white50,
                    topLeft = Offset(
                        x = max(
                            a = 0f,
                            b = min(
                                a = x - point.label.size.center.x,
                                b = size.width - point.label.size.width
                            )
                        ),
                        y = size.height - point.label.size.height,
                    )
                )
            }
        }
    }
}

private val tooltipPaddingHorizontal = 15.dp
private val tooltipPaddingVertical = 9.dp
private val tooltipCornerRadius = 6.dp
private val tooltipVerticalOffset = 6.dp
private val tooltipHorizontalOffset = 20.dp

private fun DrawScope.drawTooltip(
    measuredText: TextLayoutResult,
    point: Offset,
) {
    val tooltipSize = Size(
        measuredText.size.width + tooltipPaddingHorizontal.times(2).toPx(),
        measuredText.size.height + tooltipPaddingVertical.times(2).toPx(),
    )

    val tooltipMinX = -(tooltipHorizontalOffset.toPx())
    val tooltipMaxX = size.width + tooltipHorizontalOffset.toPx() - tooltipSize.width

    val tooltipTopLeftX = (point.x - (measuredText.size.width / 2) - tooltipPaddingHorizontal.toPx())
        .coerceIn(tooltipMinX, tooltipMaxX)
    val tooltipTopLeftY = point.y - tooltipVerticalOffset.toPx() - measuredText.size.height -
            tooltipTriangleHeight.toPx() - (tooltipPaddingVertical.toPx() * 2)

    val topLeftTooltip = Offset(
        tooltipTopLeftX,
        tooltipTopLeftY,
    )
    drawRoundRect(
        color = yellow_9,
        topLeft = topLeftTooltip,
        size = tooltipSize,
        cornerRadius = CornerRadius(tooltipCornerRadius.toPx(), tooltipCornerRadius.toPx()),
    )
    drawTriangle(
        offset = Offset(
            x = point.x - tooltipTriangleWidth.toPx() / 2,
            y = tooltipTopLeftY + tooltipSize.height - 3.5.dp.toPx(),
        ),
        size = Size(tooltipTriangleWidth.toPx(), tooltipTriangleHeight.toPx()),
        color = yellow_9,
    )

    val topLeftText = Offset(
        tooltipTopLeftX + tooltipPaddingHorizontal.toPx(),
        tooltipTopLeftY + tooltipPaddingVertical.toPx(),
    )
    drawText(
        textLayoutResult = measuredText,
        color = white,
        topLeft = topLeftText
    )
}

internal data class ChartPoint(
    val value: Float,
    val label: TextLayoutResult,
    val active: Boolean,
    val displayValue: TextLayoutResult,
)

@Composable
@Preview
private fun ChartPreview() {
    AppTheme(Themes.bland()) {
        Surface(color = AppTheme.colors.primary) {
            CaloriesChart(
                state = TodayUiState.preview,
                onAddFoodClicked = {}
            )
        }
    }
}
